import React, { useCallback, useState, useMemo } from 'react';
import {
  Button,
  ScrollableViewport,
  FormSearchWithDropdown,
} from '@ihsm/ui-shared';
import { ValidationFormField } from 'shared/types';
import { FormFailedIconMessage } from 'shared/components/FormFailedIconMessage';
import { ListWithItemDrag } from 'shared/components/ListWithRowDrag';
import { useAppSelector } from 'state/store';
import { refPricingSelector } from 'state/store/selectors';
import { useGetRefPricingQuery } from 'services/newApi/configsApi';
import { GroupDraggableItem } from './GroupDraggableItem';
import { TABS_COLUMN_OPTIONS } from '../constants';
import { FormColumnItem } from '../types';
import { NoReportData } from './NoReportData';
import { ColumnsTabsSelection } from './ColumnsTabsSelection';
import './GroupSelectionArea.scss';

interface GroupSelectionAreaProps {
  currentData: FormColumnItem[];
  onDataChange: (list: FormColumnItem[]) => void;
  isDisabled?: boolean;
  validation?: ValidationFormField;
}

export const GroupSelectionArea: React.FC<GroupSelectionAreaProps> = ({
  currentData = [],
  onDataChange = () => {},
  isDisabled = false,
  validation,
}) => {
  useGetRefPricingQuery();

  const refPricing = refPricingSelector(useAppSelector((state) => state));

  const [isOnMultipleSelection, setIsMultipleSelection] = useState(false);
  const [selectedList, setSelectedList] = useState<FormColumnItem[]>([]);
  const [selectedTab, setSelectedTab] = useState(TABS_COLUMN_OPTIONS[0]);
  const [search, setSearch] = useState<string>();

  const searchOptions = useMemo(
    () =>
      refPricing
        .filter((option) => option.category === 'Ref Data')
        .map((option) => `${option.category} / ${option.name}`),
    [refPricing]
  );

  const groupsList = useMemo(
    () =>
      refPricing.filter(
        (categoryItem) => categoryItem.category === selectedTab
      ),
    [refPricing, selectedTab]
  );

  const handleDataChange = useCallback(
    (newData: FormColumnItem[]) => {
      onDataChange(newData);
    },
    [onDataChange]
  );

  const handleDeletion = useCallback(
    (index: number) => {
      const updated = [...currentData].filter((_, i) => i !== index);
      onDataChange(updated);
    },
    [currentData, onDataChange]
  );

  const handleSearch = (value?: string) => {
    setSearch(value);
  };

  const handleAddNew = useCallback(() => {
    if (search && searchOptions.includes(search)) {
      const [category, name] = search.split(' / ');
      const found = refPricing.find(
        (item) => item.name === name && item.category === category
      );

      if (found) {
        const updated = [...currentData, found];
        onDataChange(updated);
        setSearch('');
      }
    } else {
      setIsMultipleSelection(true);
    }
  }, [currentData, onDataChange, refPricing, search, searchOptions]);

  const handleSelectionChange = useCallback(
    (newData: FormColumnItem[]) => {
      if (newData && Array.isArray(newData) && newData.length > 0) {
        const cleanedList = selectedList.filter(
          (columnItem) => columnItem.category !== newData[0].category
        );
        setSelectedList([...cleanedList, ...newData]);
      }
    },
    [selectedList]
  );

  const handleAddMultipleFromTabs = useCallback(() => {
    onDataChange([...currentData, ...selectedList]);
    setSelectedList([]);
    setIsMultipleSelection(false);
  }, [currentData, onDataChange, selectedList]);

  return (
    <div className="group-selection-area">
      <h4 className="group-selection-area__title">Groups</h4>

      <div className="group-selection-area__search-area">
        <FormSearchWithDropdown
          searchInput={search}
          setSearchInput={setSearch}
          placeholder="Search"
          optionsList={searchOptions}
          onSearch={handleSearch}
          disabled={isDisabled}
          actionButton={
            isOnMultipleSelection ? (
              <>
                <Button
                  text="&times; Cancel"
                  onClick={() => setIsMultipleSelection(false)}
                />
                <Button
                  onClick={handleAddMultipleFromTabs}
                  text="+ Add Selected"
                />
              </>
            ) : (
              <Button
                onClick={handleAddNew}
                text="+ Add Group"
                disabled={isDisabled}
              />
            )
          }
        />
      </div>

      {!isOnMultipleSelection ? (
        <>
          <ScrollableViewport vertical>
            <div
              className={`group-selection-area__draggable-box ${
                validation?.isInvalid
                  ? 'group-selection-area__draggable-box--invalid'
                  : ''
              }`}
            >
              {currentData.length < 1 ? (
                <NoReportData sectionType="Group" />
              ) : null}

              <ListWithItemDrag
                keyExpr="name"
                data={currentData}
                onDataChange={handleDataChange}
                disabled={isDisabled}
                itemTemplate={({ name, category }: FormColumnItem, index) => (
                  <GroupDraggableItem
                    name={name}
                    category={category}
                    index={index}
                    onRemove={() => handleDeletion(index)}
                  />
                )}
              />
            </div>
          </ScrollableViewport>

          <FormFailedIconMessage
            message={validation?.message}
            isActive={validation?.isInvalid}
          />
        </>
      ) : (
        <ColumnsTabsSelection
          tabs={[TABS_COLUMN_OPTIONS[0]]}
          selectedTab={selectedTab}
          onChangeTab={(tab) => setSelectedTab(tab)}
          listData={groupsList}
          selectedItems={selectedList}
          onDataChange={handleSelectionChange}
        />
      )}
    </div>
  );
};
